/**
 * Product Tags Configuration
 *
 * Centralized configuration for product tags/attributes.
 * Single source of truth for tag definitions, options, and display settings.
 *
 * This config drives:
 * - Admin edit modal form fields
 * - Tag badge display formatting
 * - Validation rules
 * - Display labels and descriptions
 *
 * To add a new tag:
 * 1. Add the type to ProductTagsData in src/types/productTags.ts
 * 2. Add the definition to PRODUCT_TAG_DEFINITIONS below
 * 3. Add options array if it's an enum type
 */

import type { FocusType, RiskLevel, RiskTypeValue } from '../types/productTags';
import { formatNumber } from '../utils/numberUtils';

// ============================================================
// Tag Field Type
// ============================================================

/**
 * Supported field types for tag definitions.
 * Determines which form control and validation to use.
 */
export type TagFieldType = 'number' | 'enum' | 'boolean' | 'string' | 'currency';

// ============================================================
// Tag Definition Interface
// ============================================================

/**
 * Definition for a single tag field.
 * Used to generate forms, validate input, and display tags.
 */
export interface TagDefinition {
  /** Unique key matching the field name in ProductTagsData */
  key: string;

  /** Human-readable display label */
  label: string;

  /** Field type determining form control and validation */
  type: TagFieldType;

  /** Options for enum types */
  options?: readonly string[];

  /** Minimum value for number/currency types */
  min?: number;

  /** Maximum value for number/currency types */
  max?: number;

  /** Whether this tag is required (all optional for v1) */
  required?: boolean;

  /** Placeholder text for form inputs */
  placeholder?: string;

  /** Help text / description shown below the input */
  description?: string;

  /** Short description for info modal display */
  infoDescription?: string;
}

// ============================================================
// Enum Options
// ============================================================

/**
 * Available options for Investment Focus tag.
 * Order matters - displayed in this order in dropdowns.
 */
export const FOCUS_OPTIONS: readonly FocusType[] = [
  'Aggressive Growth',
  'Growth',
  'Balanced',
  'Income',
  'Capital Preservation',
] as const;

/**
 * Available options for Risk Type tag.
 * Order matters - from highest to lowest risk.
 */
export const RISK_TYPE_OPTIONS: readonly RiskTypeValue[] = [
  'Extremely High',
  'High',
  'Medium',
  'Low',
  'Very Low',
] as const;

// ============================================================
// Tag Definitions
// ============================================================

/**
 * Complete definitions for all product tags.
 * Used by both basket and portfolio - same tag set for both.
 *
 * Order matters - determines display order in forms and badges.
 */
export const PRODUCT_TAG_DEFINITIONS: readonly TagDefinition[] = [
  {
    key: 'risk',
    label: 'Risk Score',
    type: 'number',
    min: 1,
    max: 10,
    placeholder: '1-10',
    description: 'Risk score from 1 (lowest) to 10 (highest)',
    infoDescription: 'Indicates the overall risk level of this product.',
  },
  {
    key: 'focus',
    label: 'Investment Focus',
    type: 'enum',
    options: FOCUS_OPTIONS,
    placeholder: 'Select focus',
    description: 'The primary investment strategy or approach',
    infoDescription: 'Describes the investment strategy and objectives.',
  },
  {
    key: 'riskType',
    label: 'Risk Type',
    type: 'enum',
    options: RISK_TYPE_OPTIONS,
    placeholder: 'Select risk type',
    description: 'Descriptive risk classification',
    infoDescription: 'Categorizes the risk profile of this product.',
  },
  {
    key: 'minInvestment',
    label: 'Minimum Investment',
    type: 'currency',
    min: 0,
    placeholder: '$0',
    description: 'Minimum investment amount in USD',
    infoDescription: 'Recommended minimum investment to achieve proper diversification.',
  },
  {
    key: 'featured',
    label: 'Featured',
    type: 'boolean',
    description: 'Show in featured/promoted section',
    infoDescription: 'This product is highlighted by Plutus.',
  },
] as const;

// ============================================================
// Tag Lookup Helpers
// ============================================================

/**
 * Get a tag definition by its key.
 *
 * @param key - The tag key to look up
 * @returns The tag definition or undefined if not found
 */
export function getTagDefinition(key: string): TagDefinition | undefined {
  return PRODUCT_TAG_DEFINITIONS.find((def) => def.key === key);
}

/**
 * Get the display label for a tag key.
 *
 * @param key - The tag key
 * @returns The human-readable label or the key if not found
 */
export function getTagLabel(key: string): string {
  const def = getTagDefinition(key);
  return def?.label ?? key;
}

/**
 * Get the info description for a tag key.
 *
 * @param key - The tag key
 * @returns The info description or empty string if not found
 */
export function getTagInfoDescription(key: string): string {
  const def = getTagDefinition(key);
  return def?.infoDescription ?? '';
}

// ============================================================
// Display Formatting Helpers
// ============================================================

/**
 * Format a currency value for display.
 * Uses USD formatting with no decimals for cleaner display.
 *
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g., "$40,000")
 */
export function formatCurrencyTag(value: number | undefined): string {
  if (value === undefined || value === null) {
    return '';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a currency value for compact badge display.
 * Uses abbreviated format for large numbers.
 *
 * @param value - The numeric value to format
 * @returns Compact formatted string (e.g., "$40k", "$1.5M")
 */
export function formatCurrencyTagCompact(value: number | undefined): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (value >= 1000000) {
    const millions = value / 1000000;
    // formatNumber handles decimals properly - 0 decimals if whole, 1 if fractional
    return `$${formatNumber(millions, millions % 1 === 0 ? 0 : 1)}M`;
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    return `$${formatNumber(thousands, thousands % 1 === 0 ? 0 : 1)}k`;
  }
  return `$${formatNumber(value, 0)}`;
}

/**
 * Format a risk score for badge display.
 *
 * @param score - Risk score from 1-10
 * @returns Formatted string (e.g., "7/10")
 */
export function formatRiskScore(score: number | undefined): string {
  if (score === undefined || score === null) {
    return '';
  }
  return `${score}/10`;
}

// ============================================================
// Color/Style Helpers
// ============================================================

/**
 * Badge color configuration for each risk level.
 * Uses Tailwind classes for consistent styling.
 * Includes both light and dark mode variants.
 */
export const RISK_LEVEL_COLORS: Record<RiskLevel, { 
  light: { bg: string; text: string; border: string };
  dark: { bg: string; text: string; border: string };
}> = {
  low: {
    light: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    dark: { bg: 'bg-green-900/50', text: 'text-green-300', border: 'border-green-700' },
  },
  medium: {
    light: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
    dark: { bg: 'bg-yellow-900/50', text: 'text-yellow-300', border: 'border-yellow-700' },
  },
  high: {
    light: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
    dark: { bg: 'bg-red-900/50', text: 'text-red-300', border: 'border-red-700' },
  },
} as const;

/**
 * Badge color configuration for non-risk tags.
 * Includes both light and dark mode variants.
 */
export const TAG_COLORS = {
  focus: {
    light: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
    dark: { bg: 'bg-purple-900/50', text: 'text-purple-300', border: 'border-purple-700' },
  },
  minInvestment: {
    light: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    dark: { bg: 'bg-blue-900/50', text: 'text-blue-300', border: 'border-blue-700' },
  },
  featured: {
    light: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
    dark: { bg: 'bg-yellow-900/50', text: 'text-yellow-300', border: 'border-yellow-700' },
  },
} as const;

/**
 * Get Tailwind classes for a risk level badge.
 *
 * @param level - The risk level category
 * @param isDarkMode - Whether dark mode is active
 * @returns Object with bg, text, and border class strings
 */
export function getRiskLevelClasses(level: RiskLevel, isDarkMode = false): { bg: string; text: string; border: string } {
  const colors = RISK_LEVEL_COLORS[level];
  return isDarkMode ? colors.dark : colors.light;
}

/**
 * Get combined Tailwind class string for a risk level badge.
 *
 * @param level - The risk level category
 * @param isDarkMode - Whether dark mode is active
 * @returns Combined class string for badge styling
 */
export function getRiskLevelClassString(level: RiskLevel, isDarkMode = false): string {
  const colors = getRiskLevelClasses(level, isDarkMode);
  return `${colors.bg} ${colors.text} ${colors.border}`;
}

/**
 * Get Tailwind classes for a specific tag type.
 *
 * @param tagKey - The tag key (e.g., 'focus', 'minInvestment')
 * @param isDarkMode - Whether dark mode is active
 * @returns Object with bg, text, and border class strings
 */
export function getTagClasses(tagKey: string, isDarkMode = false): { bg: string; text: string; border: string } {
  if (tagKey in TAG_COLORS) {
    const colors = TAG_COLORS[tagKey as keyof typeof TAG_COLORS];
    return isDarkMode ? colors.dark : colors.light;
  }
  // Default styling for unknown tags
  return isDarkMode
    ? { bg: 'bg-gray-800', text: 'text-gray-300', border: 'border-gray-700' }
    : { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };
}

// ============================================================
// Validation Helpers
// ============================================================

/**
 * Validate a risk score value.
 *
 * @param value - The value to validate
 * @returns Error message or null if valid
 */
export function validateRiskScore(value: number | undefined): string | null {
  if (value === undefined || value === null) {
    return null; // Optional field
  }
  if (!Number.isInteger(value)) {
    return 'Risk score must be a whole number';
  }
  if (value < 1 || value > 10) {
    return 'Risk score must be between 1 and 10';
  }
  return null;
}

/**
 * Validate a minimum investment value.
 *
 * @param value - The value to validate
 * @returns Error message or null if valid
 */
export function validateMinInvestment(value: number | undefined): string | null {
  if (value === undefined || value === null) {
    return null; // Optional field
  }
  if (value < 0) {
    return 'Minimum investment cannot be negative';
  }
  if (!Number.isFinite(value)) {
    return 'Invalid investment amount';
  }
  return null;
}

/**
 * Validate a focus type value.
 *
 * @param value - The value to validate
 * @returns Error message or null if valid
 */
export function validateFocus(value: string | undefined): string | null {
  if (value === undefined || value === null || value === '') {
    return null; // Optional field
  }
  if (!FOCUS_OPTIONS.includes(value as FocusType)) {
    return 'Invalid investment focus value';
  }
  return null;
}

/**
 * Validate a risk type value.
 *
 * @param value - The value to validate
 * @returns Error message or null if valid
 */
export function validateRiskType(value: string | undefined): string | null {
  if (value === undefined || value === null || value === '') {
    return null; // Optional field
  }
  if (!RISK_TYPE_OPTIONS.includes(value as RiskTypeValue)) {
    return 'Invalid risk type value';
  }
  return null;
}
